// The admin console's Applications tab (.design-specs/apply.plan.md): review /apply submissions and approve,
// waitlist or deny them. The list on top; choosing a row opens its review card below (`?id=` in the URL, which the
// new-application alert email links to).
//
// The one manual check is by eye: the reviewer opens the artist's Spotify page and confirms its Instagram link is
// the account the applicant connected. Spotify does not expose that link to apps, so the card can only point at it.
import { useEffect, useState } from "react";
import { Button, Card, DataTable, EmptyState, Field, Tag, Textarea, AcDialog, type DataTableColumn, type TagTone } from "@cntrl/ui";
import { errorMessage, formErrorMessage } from "../../app/hooks/apiError.js";
import {
  useAdminApplication,
  useAdminApplications,
  useReviewApplication,
  type AdminApplicationDetail,
  type AdminApplicationRow,
} from "../../app/hooks/useAdmin.js";
import { instagramProfileUrl, spotifyArtistUrl } from "../../app/vendorLinks.js";
import { ago } from "./adminFormat.js";
import styles from "./AdminDashboard.module.scss";

const FILTERS: ReadonlyArray<{ key: string | undefined; label: string }> = [
  { key: "pending", label: "Pending" },
  { key: "waitlisted", label: "Waitlisted" },
  { key: "approved", label: "Approved" },
  { key: "denied", label: "Denied" },
  { key: undefined, label: "All" },
];

const STATUS_TONE: Record<string, TagTone> = {
  pending: "warning",
  waitlisted: "muted",
  approved: "success",
  denied: "danger",
};

const TYPE_LABEL: Record<string, string> = {
  band: "Band",
  label: "Label",
  management: "Management",
  booking: "Booking agency",
};

const PLAN_LABEL: Record<string, string> = {
  artist_cntrl_annual: "Artist CNTRL · $79/mo annual",
  artist_cntrl_monthly: "Artist CNTRL · $99/mo",
  artist_cntrl_pro_annual: "Artist CNTRL Pro · $150/mo annual",
  artist_cntrl_pro_monthly: "Artist CNTRL Pro · $175/mo",
};

export interface ApplicationsTabProps {
  applicationId?: string;
  onSelect: (applicationId: string | undefined) => void;
}

export function ApplicationsTab({ applicationId, onSelect }: ApplicationsTabProps) {
  const [status, setStatus] = useState<string | undefined>("pending");
  const list = useAdminApplications(status);
  const rows = list.data ?? [];

  const columns: DataTableColumn<AdminApplicationRow>[] = [
    {
      key: "name",
      header: "Applicant",
      cell: (r) => (
        <button type="button" className={styles.linkButton} onClick={() => onSelect(r.applicationId)}>
          {r.accountName}
        </button>
      ),
    },
    { key: "type", header: "Type", cell: (r) => TYPE_LABEL[r.accountType ?? ""] ?? r.accountType },
    { key: "email", header: "Email", cell: (r) => r.accountEmail },
    { key: "spotify", header: "Spotify", cell: (r) => r.spotifyArtistName ?? "—" },
    { key: "instagram", header: "Instagram", cell: (r) => (r.instagramUsername ? `@${r.instagramUsername}` : "—") },
    {
      key: "status",
      header: "Status",
      cell: (r) => <Tag tone={STATUS_TONE[r.status ?? ""] ?? "neutral"} size="sm">{r.status}</Tag>,
    },
    { key: "created", header: "Applied", cell: (r) => ago(r.createdOn) },
  ];

  return (
    <>
      <Card title="Applications" subtitle="Submitted at /apply. Approving creates the account and emails them.">
        <div className={styles.controls}>
          {FILTERS.map((f) => (
            <Button key={f.label} size="sm" variant={status === f.key ? "primary" : "ghost"} onClick={() => setStatus(f.key)}>
              {f.label}
            </Button>
          ))}
        </div>
        {list.isLoading ? (
          <p className={styles.zero}>Loading…</p>
        ) : list.isError ? (
          <EmptyState title="Couldn't load applications" description={errorMessage(list.error, "Please try again.")} />
        ) : rows.length === 0 ? (
          <EmptyState title="Nothing here" description="No applications with this status." />
        ) : (
          <DataTable columns={columns} rows={rows} rowKey={(r) => r.applicationId ?? r.accountEmail ?? ""} label="Applications" />
        )}
      </Card>

      {applicationId && <ApplicationReviewCard applicationId={applicationId} onClose={() => onSelect(undefined)} />}
    </>
  );
}

type Confirm = "approve" | "waitlist" | "deny" | null;

function ApplicationReviewCard({ applicationId, onClose }: { applicationId: string; onClose: () => void }) {
  const detail = useAdminApplication(applicationId);
  const review = useReviewApplication(applicationId);
  const [notes, setNotes] = useState("");
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [confirm, setConfirm] = useState<Confirm>(null);
  const [error, setError] = useState<string | null>(null);

  const a = detail.data;
  useEffect(() => {
    if (!a) return;
    setNotes(a.reviewNotes ?? "");
    setName(a.accountName ?? "");
    setSlug(a.suggestedSlug ?? "");
  }, [a?.applicationId]);   // eslint-disable-line react-hooks/exhaustive-deps -- reseed only when the application changes

  if (detail.isLoading) return <Card title="Application"><p className={styles.zero}>Loading…</p></Card>;
  if (detail.isError || !a)
    return (
      <Card title="Application">
        <EmptyState title="Couldn't load this application" description={errorMessage(detail.error, "Please try again.")} />
      </Card>
    );

  const open = a.status === "pending" || a.status === "waitlisted";
  const isArtist = a.mode === "artist";
  const cm = a.chartmetric;
  const listed = cm?.instagramHandle ?? null;
  const connected = a.instagramUsername ?? null;
  const match = listed && connected ? listed.toLowerCase() === connected.toLowerCase() : null;
  const answers = (a.answers ?? {}) as Record<string, unknown>;
  const traction = (answers.traction ?? {}) as Record<string, string | null>;

  const run = (r: Parameters<typeof review.mutate>[0], done?: () => void) => {
    setError(null);
    review.mutate(r, {
      onSuccess: () => {
        setConfirm(null);
        done?.();
      },
      onError: (err) => {
        setConfirm(null);
        setError(formErrorMessage(err, "That didn't work."));
      },
    });
  };

  return (
    <Card
      title={a.accountName ?? "Application"}
      subtitle={`${TYPE_LABEL[a.accountType ?? ""] ?? a.accountType} · applied ${ago(a.createdOn)}`}
      actions={
        <span className={styles.inline}>
          <Tag tone={STATUS_TONE[a.status ?? ""] ?? "neutral"} size="sm">{a.status}</Tag>
          <Button size="sm" variant="ghost" onClick={onClose}>Close</Button>
        </span>
      }
    >
      <div className={styles.stack}>
        <dl className={styles.review}>
          {isArtist && (
            <>
              <dt>Spotify</dt>
              <dd>
                {a.spotifyArtistName ?? "—"}
                {cm?.monthlyListeners != null && ` · ${cm.monthlyListeners.toLocaleString()} monthly listeners`}
                {a.spotifyArtistId && (
                  <>
                    {" · "}
                    <a href={spotifyArtistUrl(a.spotifyArtistId)} target="_blank" rel="noreferrer">Open Spotify page</a>
                  </>
                )}
              </dd>
              <dt>Instagram</dt>
              <dd>
                {connected ? (
                  <>
                    Connected{" "}
                    <a href={instagramProfileUrl(connected)} target="_blank" rel="noreferrer">@{connected}</a>
                    {listed ? (
                      <>
                        {" · Chartmetric lists "}
                        <a href={instagramProfileUrl(listed)} target="_blank" rel="noreferrer">@{listed}</a>{" "}
                        <Tag tone={match ? "success" : "warning"} size="sm">{match ? "match" : "different"}</Tag>
                      </>
                    ) : (
                      " · Chartmetric lists no Instagram"
                    )}
                  </>
                ) : (
                  "Not connected"
                )}
                <div className={styles.sub}>
                  Check the Instagram link in the About section of the Spotify page matches the connected account.
                </div>
              </dd>
            </>
          )}
          {!cm && isArtist && (
            <>
              <dt>Chartmetric</dt>
              <dd>Chartmetric doesn't track this artist (normal for a small band).</dd>
            </>
          )}
          {cm?.website && (<><dt>Website</dt><dd><a href={cm.website} target="_blank" rel="noreferrer">{cm.website}</a></dd></>)}
          {(cm?.recordLabel || cm?.bookingAgent || cm?.generalManager) && (
            <>
              <dt>Team (Chartmetric)</dt>
              <dd>
                {[cm.recordLabel && `Label: ${cm.recordLabel}`, cm.bookingAgent && `Agent: ${cm.bookingAgent}`,
                  cm.generalManager && `Manager: ${cm.generalManager}`].filter(Boolean).join(" · ")}
              </dd>
            </>
          )}
          <dt>{isArtist ? "Band's email" : "Contact email"}</dt>
          <dd>{a.accountEmail}</dd>
          {isArtist && (
            <>
              <dt>Applicant</dt>
              <dd>{applicantLine(a)}</dd>
            </>
          )}
          {isArtist && a.linkOrgName && (
            <>
              <dt>Links</dt>
              <dd>{a.linkOrgName} (management), linked to the band on approval</dd>
            </>
          )}
          {!isArtist && (
            <>
              <dt>Applicant</dt>
              <dd>
                {[a.applicantFirstName, a.applicantLastName].filter(Boolean).join(" ")}
                {typeof answers.companyRole === "string" && ` · ${answers.companyRole}`}
              </dd>
            </>
          )}
          {a.city && (<><dt>City</dt><dd>{a.city}</dd></>)}
          {a.planKey && (<><dt>Plan</dt><dd>{PLAN_LABEL[a.planKey] ?? a.planKey}</dd></>)}
          {/* Genres, traction, "winning on" and team are BAND questions (ART-306): a company is asked only its first
              section, and a company application sent before that change carries the form's defaults there. */}
          {isArtist && (a.genres?.length ?? 0) > 0 && (<><dt>Genres</dt><dd>{a.genres!.join(", ")}</dd></>)}
          {isArtist && Object.values(traction).some(Boolean) && (
            <>
              <dt>Traction</dt>
              <dd>
                {[
                  traction.spotifyListeners && `1K+ Spotify listeners: ${traction.spotifyListeners}`,
                  traction.instagramFollowers && `1K+ IG followers: ${traction.instagramFollowers}`,
                  traction.playingShows && `Playing shows: ${traction.playingShows}`,
                  traction.touringVehicle && `Vehicle: ${traction.touringVehicle}`,
                  traction.roomSize && `Rooms: ${traction.roomSize}`,
                  traction.crewSize && `Crew: ${traction.crewSize}`,
                ].filter(Boolean).join(" · ")}
              </dd>
            </>
          )}
          {(cm?.instagramFollowers != null || cm?.spotifyFollowers != null) && (
            <>
              <dt>Actual (Chartmetric)</dt>
              <dd>
                {[cm.spotifyFollowers != null && `${cm.spotifyFollowers.toLocaleString()} Spotify followers`,
                  cm.instagramFollowers != null && `${cm.instagramFollowers.toLocaleString()} IG followers`]
                  .filter(Boolean).join(" · ")}
              </dd>
            </>
          )}
          {isArtist && (typeof answers.topSocial === "string" || typeof answers.topStreaming === "string") && (
            <>
              <dt>Winning on</dt>
              <dd>{[answers.topSocial, answers.topStreaming].filter((x) => typeof x === "string").join(" · ")}</dd>
            </>
          )}
          {isArtist && Array.isArray(answers.team) && answers.team.length > 0 && (
            <><dt>Team</dt><dd>{(answers.team as string[]).join(", ")}</dd></>
          )}
          {typeof answers.website === "string" && (<><dt>Website (given)</dt><dd>{answers.website}</dd></>)}
          {typeof answers.rosterSize === "string" && (<><dt>Roster size</dt><dd>{answers.rosterSize} artists</dd></>)}
          {a.reviewedOn && (<><dt>Reviewed</dt><dd>{ago(a.reviewedOn)}{a.reviewedBy && ` by ${a.reviewedBy}`}</dd></>)}
        </dl>

        <Textarea label="Notes" value={notes} rows={3} onChange={(e) => setNotes(e.target.value)}
          placeholder="What you checked, who you contacted, what they said." />
        <div>
          <Button size="sm" variant="ghost" loading={review.isPending && review.variables?.action === "notes"}
            disabled={notes === (a.reviewNotes ?? "")} onClick={() => run({ action: "notes", notes })}>
            Save notes
          </Button>
        </div>

        {open && (
          <>
            {isArtist ? (
              <div className={styles.controls}>
                <Field label="Band name" value={name} onChange={(e) => setName(e.target.value)} />
                <Field label="URL" value={slug} hint={`artistcntrl.app/${slug || "…"}`} onChange={(e) => setSlug(e.target.value)} />
              </div>
            ) : (
              <Field label="Company name" value={name} onChange={(e) => setName(e.target.value)} />
            )}
            <span className={styles.inline}>
              <Button size="sm" variant="primary" onClick={() => setConfirm("approve")}>Approve</Button>
              {a.status === "pending" && (
                <Button size="sm" variant="ghost" onClick={() => setConfirm("waitlist")}>Waitlist</Button>
              )}
              <Button size="sm" variant="ghost" onClick={() => setConfirm("deny")}>Deny</Button>
            </span>
          </>
        )}
        {error && <p className={styles.error} role="alert">{error}</p>}
      </div>

      {confirm && (
        <AcDialog
          title={confirm === "approve" ? "Approve this application?" : confirm === "waitlist" ? "Waitlist this application?" : "Deny this application?"}
          message={confirmMessage(confirm, a, name)}
          confirmLabel={confirm === "approve" ? "Approve" : confirm === "waitlist" ? "Waitlist" : "Deny"}
          tone={confirm === "deny" ? "danger" : undefined}
          loading={review.isPending}
          onConfirm={() =>
            run(confirm === "approve" ? { action: "approve", name: name.trim(), slug: slug.trim() || undefined } : { action: confirm })
          }
          onCancel={() => setConfirm(null)}
        />
      )}
    </Card>
  );
}

/**
 * Who applied for an artist. Two shapes reach this screen: since ART-307 every artist application carries the
 * applicant's own address and a role (a band title, or Label / Management / Booking) and says whether they were
 * signed in; one sent before that carries `emailKind` — "artist" (the one address was the band's, no applicant) or
 * "personal" (the applicant's address + their band title).
 */
function applicantLine(a: AdminApplicationDetail): string {
  if (!a.applicantEmail) return a.emailKind === "artist" ? "Applied from the band's own address" : "—";
  const role = a.applicantOrgRole ? ORG_ROLE_LABEL[a.applicantOrgRole] ?? a.applicantOrgRole : a.applicantTitleLabel;
  return [a.applicantEmail, role, a.applicantSignedIn ? "signed in" : null].filter(Boolean).join(" · ");
}

const ORG_ROLE_LABEL: Record<string, string> = { label: "Label", management: "Management", booking: "Booking" };

function confirmMessage(confirm: Exclude<Confirm, null>, a: AdminApplicationDetail, name: string): string {
  const to = [a.accountEmail, a.applicantEmail].filter((x, i, all) => !!x && all.indexOf(x) === i).join(" and ");
  const bandInvite = a.applicantEmail && a.applicantTitleKey ? ` ${a.applicantEmail} gets an invite to join the band.` : "";
  const link = a.linkOrgName
    ? ` ${a.linkOrgName} is linked to the band as its management (at once if the applicant runs its roster, otherwise as a request to the company).`
    : "";
  if (confirm === "approve")
    return a.mode === "artist"
      ? `Creates ${name || a.accountName} with an artist account on ${a.accountEmail}, carries the Instagram connection over, and emails a set-password link.${bandInvite}${link}`
      : `Creates ${name || a.accountName} with ${a.accountEmail} as its admin and emails them.`;
  if (confirm === "waitlist") return `Emails ${to} that they're on the waitlist. You can still approve it later.`;
  return `Emails ${to} that the application wasn't approved. Replies go to the applications inbox.`;
}
